import * as React from 'react'
import { StoreProps } from '../../../../services/stores/casting.store'
import { FloatingContainerTitle } from '../casting-styled.component'
import { I18nService } from '../../../../services/utils/i18n.service'
import { StyledDayPicker, getDayPickerDateLocale } from '../../../shared/styled-day-picker.component'
import { moment } from '../../../../services/utils/moment.service'
import LoadingComponent from '../../../shared/loading.component'
import { SupplierCastingsGetGroupSessionsAtDay } from '../../../../services/json-schemas/serializers/supplier-castings-get-group-sessions-at-day'
import { scheduleI18nIntervalFrequency } from '../../../../services/models/schedules.service'
import SelectableListComponent from '../../../shared/selectable-list.component'

const i18nScoped = new I18nService(require('./group-session-picker.component.yml'))

type GroupSession = SupplierCastingsGetGroupSessionsAtDay['group_sessions'][0]

export default function GroupSessionPickerComponent ({ store, data: { slots, groupSessionSchedule, castingType } }: StoreProps) {
  const fromMonth = React.useMemo(() => moment().startOf('month').toDate(), [])
  const startDate = React.useMemo(() => slots[0]?.start_date ? moment(slots[0].start_date).toDate() : null, [slots])

  // Track the GroupSession on the displayed month => only days with sessions are allowed.
  const [displayMonth, setDisplayMonth] = React.useState(() => startDate || fromMonth)

  // Get available group session dates (fast, for day picker)
  const groupSessionDates = store.getGroupSessionDates()
  
  // Get detailed group sessions for the selected day (filtered by casting type on backend)
  const dayGroupSessions = startDate ? store.getGroupSessionsAtDay(moment(startDate).format('YYYY-MM-DD')) : null
  
  const onSelectSession = React.useCallback((session: GroupSession, skipNextFocus = false) => {
    store.setGroupSession(session)
    if (!skipNextFocus) store.nextFocus({ closeOnMobile: true })
  }, [store])

  // Auto-select the group session schedule when there is exactly one session on the selected day
  React.useEffect(() => {
    if (!dayGroupSessions || dayGroupSessions.length !== 1 || groupSessionSchedule) return

    // use skipNextFocus = true to avoid automatically moving focus in mobile flows
    onSelectSession(dayGroupSessions[0], true)
  }, [dayGroupSessions, groupSessionSchedule, onSelectSession])

  const onSelectDate = React.useCallback((selectedDate: Date) => {
    if (!selectedDate) return

    // When a date is selected, we need to fetch sessions for that day
    const dayString = moment(selectedDate).format('YYYY-MM-DD')
    
    // Update slots with the selected date but no patterns yet (will be set when session is selected)
    store.setSlots([{ ...slots[0], start_date: dayString, allowed_patterns: [] }])
    store.set({ groupSessionSchedule: null })
  }, [store, slots])
  
  // Check if a day has available sessions
  const isDayDisabled = React.useCallback((day: Date) => !groupSessionDates?.includes(moment(day).format('YYYY-MM-DD')), [groupSessionDates])
  
  const formatHourRange = (session: GroupSession) => {
    const s = moment(session.start_date)
    const e = moment(session.end_date)
    const fmt = (m: moment.Moment) => m.minutes() === 0 ? `${m.format('H')}h` : `${m.format('H')}h${m.format('mm')}`
    return `${fmt(s)} - ${fmt(e)}`
  }

  const renderedGroupSessionsGrid = React.useMemo(() => {
    if (!startDate) return null
    if (!dayGroupSessions) return <LoadingComponent message={ i18nScoped.t('loading_sessions') } presentation='inline' />

    if (castingType === 'casting_one_shot' && !dayGroupSessions.some(_ => _.group_session_schedule.name)) {
      return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', marginBottom: '2em' }}>
        { dayGroupSessions.map(session => {
          const isSelected = groupSessionSchedule?.id === session.group_session_schedule?.id

          return <a
            key={ session.id }
            className={ 'group-session-schedule-choice button--S ' + (isSelected ? 'button' : 'buttonNeutral') }
            children={ formatHourRange(session) }
            onClick={ () => onSelectSession(session) } />
        }) }
      </div>
    }

    if (castingType === 'casting_one_shot') {
      return <SelectableListComponent
        name="group-session"
        data={ dayGroupSessions }
        value={ dayGroupSessions.find(session => groupSessionSchedule?.id === session.group_session_schedule?.id) || null }
        cssIdGenerator={ groupSession => `js-test-group-session-${groupSession.id}` }
        formatter={ session => <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{ session.group_session_schedule.name || i18nScoped.t('unnamed_group') }</span>
          <span>{ formatHourRange(session) }</span>
        </div> }
        onChange={ onSelectSession } />
    }

    if (castingType === 'casting_recurring') {
      return <SelectableListComponent
        name="group-session"
        data={ dayGroupSessions }
        value={ dayGroupSessions.find(session => groupSessionSchedule?.id === session.group_session_schedule?.id) || null }
        cssIdGenerator={ groupSession => `js-test-group-session-${groupSession.id}` }
        formatter={ (session) => {
          const futureExceptionTimes = session.group_session_schedule.exception_times
            .filter(exceptionTime => moment(exceptionTime).isAfter(session.start_date))
            .map(exTime => ({ type: 'cancelled', date: exTime }))
          
          const futureFullSessions = session.group_session_schedule.following_full_group_sessions
            .map(fullSession => ({ type: 'full', date: fullSession.start_date }))
          
          const allFutureIssues = [...futureExceptionTimes, ...futureFullSessions]
            .sort((a, b) => moment(a.date).diff(moment(b.date)))

          return <div>
            { session.group_session_schedule.name && <div>{ session.group_session_schedule.name }</div> }

            <div style={{ fontWeight: 'normal' }}>
              { formatHourRange(session) },&nbsp;
              { scheduleI18nIntervalFrequency(session.group_session_schedule.interval, 'weekly', { capitalize: false }) },&nbsp;
              { i18nScoped.t('until', { date: moment(session.group_session_schedule.end_date).format('ll') }) }
            </div>

            { allFutureIssues.map(issue => <div key={ issue.date } style={{ fontWeight: 'normal' }}>
              <em className="icon-close" style={{ marginRight: '0.5em', color: '#d32f2f' }} />
              { issue.type === 'cancelled' 
                ? i18nScoped.t('session_cancelled', { date: moment(issue.date).format('ll') })
                : i18nScoped.t('session_full', { date: moment(issue.date).format('ll') })
              }
            </div>) }
          </div>
        } }
        onChange={ onSelectSession } />
    }

    return null
  }, [castingType, dayGroupSessions, groupSessionSchedule?.id, onSelectSession, startDate])

  return <>
    <FloatingContainerTitle>{ i18nScoped.t('select_session_date') }</FloatingContainerTitle>
    
    { !groupSessionDates
      ? <LoadingComponent message={ i18nScoped.t('loading_sessions') } presentation='inline' />
      : groupSessionDates.length === 0
      ? <p>{ i18nScoped.t('no_sessions_available') }</p>
      : <StyledDayPicker
          mode='single'
          defaultMonth={ displayMonth }
          startMonth={ fromMonth }
          onMonthChange={ setDisplayMonth }
          locale={ getDayPickerDateLocale() }
          selected={ startDate }
          disabled={ isDayDisabled }
          onSelect={ onSelectDate } /> }

    { startDate && <FloatingContainerTitle style={{ margin: '1em 0' }}>{ i18nScoped.t('select_session_time') }</FloatingContainerTitle> }
    { renderedGroupSessionsGrid }
  </>
}